import { FC, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import axios from 'axios';
import {
  IngestionIssue,
  IngestionFileGroup,
  IngestionIssuesPaginatedResponse,
  INGESTION_ERROR_TYPES,
} from './types';
import './IngestionIssuesTable.scss';

// Serialize params with repeated-key format for arrays: { clientId: ['a','b'] } → 'clientId=a&clientId=b'
const serializeParams = (params: Record<string, string | number | boolean | string[]>): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, String(v)));
    } else if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
};

interface SessionTokens {
  jwtToken: string;
  sessionId: string;
}

interface IngestionIssuesTableProps {
  startDate: Date | null;
  endDate: Date | null;
  getSessionTokensFn: () => Promise<SessionTokens>;
  formatDateToStringFn: (date: Date | null) => string;
  fetchTrigger: number;
  selectedClients?: string[];
  initialErrorType?: string;
}

type ViewMode = 'grouped' | 'flat';

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

// Issue categories (non-blocking warnings) — distinguished from errors in the UI
const ISSUE_CATEGORIES = new Set([
  'duplicate_address',
  'no_address',
  'zero_weight',
  // 'no_vehicle_match', // Hidden — uncomment to re-enable vehicle match issues
  'no_load_carrier_match',
]);

const IngestionIssuesTable: FC<IngestionIssuesTableProps> = ({
  startDate,
  endDate,
  getSessionTokensFn,
  formatDateToStringFn,
  fetchTrigger,
  selectedClients = [],
  initialErrorType = '',
}) => {
  const { t } = useTranslation();

  // View mode
  const [viewMode, setViewMode] = useState<ViewMode>('grouped');

  // Grouped data state
  const [groupedItems, setGroupedItems] = useState<IngestionFileGroup[]>([]);
  const [groupedTotal, setGroupedTotal] = useState(0);
  const [groupedPage, setGroupedPage] = useState(1);
  const [groupedLimit, setGroupedLimit] = useState(10);
  const [groupedLoading, setGroupedLoading] = useState(false);

  // Flat data state
  const [flatItems, setFlatItems] = useState<IngestionIssue[]>([]);
  const [flatTotal, setFlatTotal] = useState(0);
  const [flatPage, setFlatPage] = useState(1);
  const [flatLimit, setFlatLimit] = useState(10);
  const [flatLoading, setFlatLoading] = useState(false);

  // Filters
  const [filenameFilter, setFilenameFilter] = useState('');
  const [errorTypeFilter, setErrorTypeFilter] = useState(initialErrorType);
  const [selectedFilename, setSelectedFilename] = useState('');

  // Sync initialErrorType prop changes
  useEffect(() => {
    setErrorTypeFilter(initialErrorType);
  }, [initialErrorType]);

  // Error state
  const [error, setError] = useState('');

  // Format date for display
  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'dd-MM-yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  // Get failure rate CSS class — hidden for now, kept for future use
  // const getFailureRateClass = (rate: number) => {
  //   const pct = rate * 100;
  //   if (pct >= 50) return 'ingestion-issues-table__failure-rate--high';
  //   if (pct >= 20) return 'ingestion-issues-table__failure-rate--medium';
  //   return 'ingestion-issues-table__failure-rate--low';
  // };

  // Fetch grouped data
  const fetchGroupedData = useCallback(
    async (page = 1, limit = 10) => {
      if (!startDate || !endDate) return;

      setGroupedLoading(true);
      setError('');

      try {
        const { jwtToken, sessionId } = await getSessionTokensFn();
        const params: Record<string, string | number | boolean | string[]> = {
          startDate: formatDateToStringFn(startDate),
          endDate: formatDateToStringFn(endDate),
          page,
          limit,
          groupByFile: true,
        };
        if (filenameFilter) params.filename = filenameFilter;
        if (errorTypeFilter) params.errorType = errorTypeFilter;
        if (selectedClients.length > 0) params.clientId = selectedClients;

        const response = await axios.get<IngestionIssuesPaginatedResponse<IngestionFileGroup>>(
          `${process.env.REACT_APP_LPI_API_URL}/quality/ingestion-issues`,
          {
            params,
            paramsSerializer: serializeParams,
            headers: {
              Authorization: `Session ${jwtToken}`,
              'X-Session-Id': sessionId,
            },
          }
        );

        if (response.data) {
          setGroupedItems(response.data.items || []);
          setGroupedTotal(response.data.total || 0);
        } else {
          setGroupedItems([]);
          setGroupedTotal(0);
        }
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch grouped ingestion issues:', err);
        setGroupedItems([]);
        setGroupedTotal(0);
        setError(err.response?.data?.message || t('cpi.ingestion.loadError'));
      } finally {
        setGroupedLoading(false);
      }
    },
    [startDate, endDate, getSessionTokensFn, formatDateToStringFn, filenameFilter, errorTypeFilter, selectedClients, t]
  );

  // Fetch flat data
  const fetchFlatData = useCallback(
    async (page = 1, limit = 10, filename = '') => {
      if (!startDate || !endDate) return;

      setFlatLoading(true);
      setError('');

      try {
        const { jwtToken, sessionId } = await getSessionTokensFn();
        const params: Record<string, string | number | string[]> = {
          startDate: formatDateToStringFn(startDate),
          endDate: formatDateToStringFn(endDate),
          page,
          limit,
        };
        if (filename) params.filename = filename;
        else if (filenameFilter) params.filename = filenameFilter;
        if (errorTypeFilter) params.errorType = errorTypeFilter;
        if (selectedClients.length > 0) params.clientId = selectedClients;

        const response = await axios.get<IngestionIssuesPaginatedResponse<IngestionIssue>>(
          `${process.env.REACT_APP_LPI_API_URL}/quality/ingestion-issues`,
          {
            params,
            paramsSerializer: serializeParams,
            headers: {
              Authorization: `Session ${jwtToken}`,
              'X-Session-Id': sessionId,
            },
          }
        );

        if (response.data) {
          setFlatItems(response.data.items || []);
          setFlatTotal(response.data.total || 0);
        } else {
          setFlatItems([]);
          setFlatTotal(0);
        }
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch flat ingestion issues:', err);
        setFlatItems([]);
        setFlatTotal(0);
        setError(err.response?.data?.message || t('cpi.ingestion.loadError'));
      } finally {
        setFlatLoading(false);
      }
    },
    [startDate, endDate, getSessionTokensFn, formatDateToStringFn, filenameFilter, errorTypeFilter, selectedClients, t]
  );

  // Auto-fetch when fetchTrigger changes (parent clicked Apply)
  useEffect(() => {
    if (fetchTrigger === 0) return;
    if (!startDate || !endDate) return;

    setGroupedPage(1);
    setFlatPage(1);
    setSelectedFilename('');

    if (viewMode === 'grouped') {
      fetchGroupedData(1, groupedLimit);
    } else {
      fetchFlatData(1, flatLimit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTrigger]);

  // Refetch when error type filter changes (only if we have data)
  useEffect(() => {
    if (fetchTrigger === 0) return;
    if (!startDate || !endDate) return;

    if (viewMode === 'grouped') {
      setGroupedPage(1);
      fetchGroupedData(1, groupedLimit);
    } else {
      setFlatPage(1);
      fetchFlatData(1, flatLimit, selectedFilename);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorTypeFilter]);

  // Handle view mode switch
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    if (fetchTrigger === 0 || !startDate || !endDate) return;

    if (mode === 'grouped') {
      if (groupedItems.length === 0 && !groupedLoading) {
        fetchGroupedData(groupedPage, groupedLimit);
      }
    } else if (flatItems.length === 0 && !flatLoading) {
      fetchFlatData(flatPage, flatLimit, selectedFilename);
    }
  };

  // Handle filename drill-down from grouped view
  const handleFilenameDrillDown = (filename: string) => {
    setSelectedFilename(filename);
    setViewMode('flat');
    setFlatPage(1);
    fetchFlatData(1, flatLimit, filename);
  };

  // Handle back to all files
  const handleBackToAllFiles = () => {
    setSelectedFilename('');
    setViewMode('grouped');
    if (groupedItems.length === 0 && !groupedLoading && fetchTrigger > 0) {
      fetchGroupedData(groupedPage, groupedLimit);
    }
  };

  // Handle filename search (on Enter)
  const handleFilenameSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && fetchTrigger > 0 && startDate && endDate) {
      if (viewMode === 'grouped') {
        setGroupedPage(1);
        fetchGroupedData(1, groupedLimit);
      } else {
        setFlatPage(1);
        fetchFlatData(1, flatLimit, selectedFilename);
      }
    }
  };

  // Grouped pagination
  const groupedTotalPages = Math.ceil(groupedTotal / Math.max(groupedLimit, 1));

  const handleGroupedPageChange = (page: number) => {
    const validPage = Math.max(1, Math.min(page, groupedTotalPages));
    setGroupedPage(validPage);
    fetchGroupedData(validPage, groupedLimit);
  };

  const handleGroupedLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = Number(e.target.value);
    setGroupedLimit(newLimit);
    setGroupedPage(1);
    fetchGroupedData(1, newLimit);
  };

  // Flat pagination
  const flatTotalPages = Math.ceil(flatTotal / Math.max(flatLimit, 1));

  const handleFlatPageChange = (page: number) => {
    const validPage = Math.max(1, Math.min(page, flatTotalPages));
    setFlatPage(validPage);
    fetchFlatData(validPage, flatLimit, selectedFilename);
  };

  const handleFlatLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = Number(e.target.value);
    setFlatLimit(newLimit);
    setFlatPage(1);
    fetchFlatData(1, newLimit, selectedFilename);
  };

  // Loading skeleton
  const isLoading = viewMode === 'grouped' ? groupedLoading : flatLoading;

  if (fetchTrigger === 0 || !startDate || !endDate) {
    return (
      <div className="ingestion-issues-table">
        <div className="ingestion-issues-table__initial-state">
          <p>{t('cpi.ingestion.selectDateRange')}</p>
        </div>
      </div>
    );
  }

  if (isLoading && ((viewMode === 'grouped' && groupedItems.length === 0) || (viewMode === 'flat' && flatItems.length === 0))) {
    return (
      <div className="ingestion-issues-table ingestion-issues-table--loading">
        <div className="ingestion-issues-table__skeleton" />
      </div>
    );
  }

  // Pagination renderer
  const renderPagination = (
    total: number,
    page: number,
    limit: number,
    totalPagesVal: number,
    onPageChange: (p: number) => void,
    onLimitChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  ) => {
    if (total <= 0) return null;
    return (
      <div className="ingestion-issues-table__pagination">
        <div className="ingestion-issues-table__pagination-info">
          {t('Showing')} {(page - 1) * limit + 1} {t('to')}{' '}
          {Math.min(page * limit, total)} {t('of')} {total} {t('entries')}
        </div>
        <div className="ingestion-issues-table__pagination-controls">
          <select
            value={limit}
            onChange={onLimitChange}
            className="ingestion-issues-table__page-size"
          >
            {ITEMS_PER_PAGE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option} {t('per page')}
              </option>
            ))}
          </select>
          <div className="ingestion-issues-table__page-nav">
            <button
              type="button"
              onClick={() => onPageChange(1)}
              disabled={page <= 1}
              className="ingestion-issues-table__page-btn"
              aria-label={t("First page")}
            >
              ««
            </button>
            <button
              type="button"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              className="ingestion-issues-table__page-btn"
              aria-label={t("Previous page")}
            >
              «
            </button>
            <span className="ingestion-issues-table__page-current">
              {page} / {totalPagesVal || 1}
            </span>
            <button
              type="button"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPagesVal}
              className="ingestion-issues-table__page-btn"
              aria-label={t("Next page")}
            >
              »
            </button>
            <button
              type="button"
              onClick={() => onPageChange(totalPagesVal)}
              disabled={page >= totalPagesVal}
              className="ingestion-issues-table__page-btn"
              aria-label={t("Last page")}
            >
              »»
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="ingestion-issues-table">
      {/* Header with title and view toggle */}
      <div className="ingestion-issues-table__header">
        <h2 className="ingestion-issues-table__title">{t('cpi.ingestion.title')}</h2>
        <div className="ingestion-issues-table__view-toggle">
          <button
            type="button"
            className={`ingestion-issues-table__view-btn ${viewMode === 'grouped' ? 'ingestion-issues-table__view-btn--active' : ''}`}
            onClick={() => handleViewModeChange('grouped')}
          >
            {t('cpi.ingestion.groupedView')}
          </button>
          <button
            type="button"
            className={`ingestion-issues-table__view-btn ${viewMode === 'flat' ? 'ingestion-issues-table__view-btn--active' : ''}`}
            onClick={() => handleViewModeChange('flat')}
          >
            {t('cpi.ingestion.flatView')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="ingestion-issues-table__filters">
        <input
          type="text"
          className="ingestion-issues-table__search-input"
          placeholder={t('cpi.ingestion.searchFilename')}
          value={filenameFilter}
          onChange={(e) => setFilenameFilter(e.target.value)}
          onKeyDown={handleFilenameSearchKeyDown}
        />
        <select
          className="ingestion-issues-table__error-type-select"
          value={errorTypeFilter}
          onChange={(e) => setErrorTypeFilter(e.target.value)}
        >
          <option value="">{t('cpi.ingestion.allErrorTypes')}</option>
          {INGESTION_ERROR_TYPES.map((type) => (
            <option key={type} value={type}>
              {t(`cpi.ingestion.errorCategory.${type}`)}
            </option>
          ))}
        </select>
      </div>

      {/* Breadcrumb for drill-down */}
      {selectedFilename && viewMode === 'flat' && (
        <div className="ingestion-issues-table__breadcrumb">
          <button
            type="button"
            className="ingestion-issues-table__breadcrumb-link"
            onClick={handleBackToAllFiles}
          >
            {t('cpi.ingestion.backToAllFiles')}
          </button>
          <span className="ingestion-issues-table__breadcrumb-separator">›</span>
          <span className="ingestion-issues-table__breadcrumb-current">{selectedFilename}</span>
        </div>
      )}

      {/* Error display */}
      {error && <div className="ingestion-issues-table__error">{error}</div>}

      {/* Grouped View Table */}
      {viewMode === 'grouped' && (
        <>
          <div className="ingestion-issues-table__wrapper">
            <table className="ingestion-issues-table__table">
              <thead>
                <tr>
                  <th>{t('cpi.ingestion.filename')}</th>
                  <th>{t('cpi.ingestion.uploadDate')}</th>
                  <th>{t('cpi.ingestion.totalOrders')}</th>
                  <th>{t('cpi.ingestion.totalShipments')}</th>
                  <th>{t('cpi.ingestion.issuesCount')}</th>
                  {/* Failure Rate hidden — misleading when issues (warnings) are included in count */}
                  <th>{t('cpi.ingestion.topErrors')}</th>
                  <th>{t('cpi.ingestion.topIssues')}</th>
                </tr>
              </thead>
              <tbody>
                {groupedItems.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="ingestion-issues-table__empty">
                      {t('cpi.ingestion.noIssues')}
                    </td>
                  </tr>
                ) : (
                  groupedItems.map((group) => (
                    <tr key={`${group.sourceFilename}-${group.uploadDate}`}>
                      <td>
                        <button
                          type="button"
                          className="ingestion-issues-table__filename-link"
                          onClick={() => handleFilenameDrillDown(group.sourceFilename)}
                        >
                          {group.sourceFilename}
                        </button>
                      </td>
                      <td>{formatDate(group.uploadDate)}</td>
                      <td>{group.totalOrders}</td>
                      <td>{group.totalShipments}</td>
                      <td>{group.failedOrders}</td>
                      {/* Failure Rate hidden */}
                      <td>
                        {group.topErrors.filter((err) => !ISSUE_CATEGORIES.has(err.errorCategory || '')).map((err, idx) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <span key={`err-${err.errorCategory}-${err.count}-${idx}`} className="ingestion-issues-table__error-badge-group">
                            {err.errorCategory && (
                              <span className={`ingestion-issues-table__category-badge ingestion-issues-table__category-badge--${err.errorCategory}`}>
                                {t(`cpi.ingestion.errorCategory.${err.errorCategory}`)} ({err.count})
                              </span>
                            )}
                          </span>
                        ))}
                      </td>
                      <td>
                        {group.topErrors.filter((err) => ISSUE_CATEGORIES.has(err.errorCategory || '')).map((err, idx) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <span key={`issue-${err.errorCategory}-${err.count}-${idx}`} className="ingestion-issues-table__error-badge-group">
                            {err.errorCategory && (
                              <span className={`ingestion-issues-table__category-badge ingestion-issues-table__category-badge--${err.errorCategory}`}>
                                {t(`cpi.ingestion.errorCategory.${err.errorCategory}`)} ({err.count})
                              </span>
                            )}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {renderPagination(
            groupedTotal,
            groupedPage,
            groupedLimit,
            groupedTotalPages,
            handleGroupedPageChange,
            handleGroupedLimitChange
          )}
        </>
      )}

      {/* Flat View Table */}
      {viewMode === 'flat' && (
        <>
          <div className="ingestion-issues-table__wrapper">
            <table className="ingestion-issues-table__table">
              <thead>
                <tr>
                  <th>{t('cpi.ingestion.orderNumber')}</th>
                  <th>{t('cpi.ingestion.volgnummer')}</th>
                  <th>{t('cpi.ingestion.errorReason')}</th>
                  <th>{t('cpi.ingestion.errorDetails')}</th>
                  <th>{t('cpi.ingestion.sourceFilename')}</th>
                  <th>{t('cpi.ingestion.uploadDate')}</th>
                </tr>
              </thead>
              <tbody>
                {flatItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="ingestion-issues-table__empty">
                      {t('cpi.ingestion.noIssues')}
                    </td>
                  </tr>
                ) : (
                  flatItems.map((issue) => (
                    <tr key={`${issue.orderNumber}-${issue.volgnummer}-${issue.errorReason}-${issue.sourceFilename}-${issue.uploadDate}`}>
                      <td>{issue.orderNumber || '-'}</td>
                      <td>{issue.volgnummer ?? '-'}</td>
                      <td>
                        {issue.errorCategory && (
                          <span className={`ingestion-issues-table__category-badge ingestion-issues-table__category-badge--${issue.errorCategory}`}>
                            {t(`cpi.ingestion.errorCategory.${issue.errorCategory}`)}
                          </span>
                        )}
                      </td>
                      <td>{issue.errorDetails || '-'}</td>
                      <td>{issue.sourceFilename || '-'}</td>
                      <td>{formatDate(issue.uploadDate)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {renderPagination(
            flatTotal,
            flatPage,
            flatLimit,
            flatTotalPages,
            handleFlatPageChange,
            handleFlatLimitChange
          )}
        </>
      )}
    </div>
  );
};

export default IngestionIssuesTable;
